import {
  Datagrid,
  List,
  TextField,
  DateField,
  TextInput,
  SelectInput,
  DateInput,
  Show,
  SimpleShowLayout,
  NumberField,
  Labeled,
  CreateButton,
  Create,
  ReferenceInput,
  required,
  NumberInput,
  SimpleForm,
  useGetOne,
  useNotify,
  useRedirect,
  Edit,
  useRecordContext,
  usePermissions,
} from "react-admin";
import { Card, Grid, Typography, Divider, Chip } from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EventNoteIcon from "@mui/icons-material/EventNote";

import { useWatch } from "react-hook-form";
import InvoiceDialog from "./components/invoice-dialog";
import NegotiationOfferDialog from "./components/negotiations-dialog";
import { useIsManager, ReadOnlyListActions } from "./components/manager-read-only";

// import * as React from 'react';

const invoiceFilters = [
  <TextInput label="Search Invoice ID" source="search" alwaysOn />,
  <SelectInput
    label="Status"
    source="status"
    choices={[
      { id: "", name: "All" },
      { id: "pending", name: "Pending" },
      { id: "accepted", name: "Accepted" },
      { id: "rejected", name: "Rejected" }
    ]}
    alwaysOn
    optionText="name"
    optionValue="id"
  />,
  <DateInput label="From Date" source="fromDate" alwaysOn />,
  <DateInput label="To Date" source="toDate" alwaysOn />,
];

export const InvoiceList = (props) => {
  const isManager = useIsManager();
  return (
  <List
    {...props}
    filters={invoiceFilters}
    actions={isManager ? <ReadOnlyListActions /> : undefined}
  >
    <Datagrid rowClick="show">
      <TextField source="id" label="Invoice ID" />
      <TextField source="negotiation.id" label="Negotiation ID" />
      <NumberField source="quantity" label="Quantity" />
      <NumberField source="amount" label="Amount" />
      <TextField source="status" label="Status" />
      <DateField source="created_at" label="Issued Date" />
      <DateField source="dueDate" label="Due Date" />
    </Datagrid>
  </List>
  );
};

const statusColors = {
  pending: "warning",
  accepted: "success",
  rejected: "error",
};

export const InvoiceShow = (props) => {
  const isManager = useIsManager();
  return (
  <Show {...props} actions={isManager ? false : undefined}>
    <SimpleShowLayout>
      {/* Invoice Details */}
      <div className="w-full border p-6 rounded-xl shadow-xl bg-[#f0f8ff]">
        <h1 className="text-xl mb-4 font-bold flex items-center">
          <ReceiptIcon className="mr-2" /> Invoice Details
        </h1>
        <Divider className="my-4" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Labeled label="Invoice ID">
            <InvoiceDialog />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Status">
              <Chip
                label={<TextField source="status" />}
                color={statusColors[props.record?.status] || "default"}
                sx={{ fontWeight: "bold" }}
              />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Issued Date">
              <DateField source="created_at" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Due Date">
              <DateField source="dueDate" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Quantity">
              <NumberField source="quantity" />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Amount">
              <NumberField source="amount" options={{ style: 'currency', currency: 'USD' }} />
            </Labeled>
          </Grid>
        </Grid>
      </div>

      {/* Negotiation Details */}
      <div className="w-full border p-6 rounded-xl shadow-xl bg-[#e8f5e9] mt-6">
        <h1 className="text-xl mb-4 font-bold flex items-center">
          <ShoppingCartIcon className="mr-2" /> Associated Negotiation
        </h1>
        <Divider className="my-4" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Labeled label="Negotiation ID">
              <NegotiationOfferDialog/>
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Labeled label="Trade Type">
              <TextField source="negotiation.tradeType" />
            </Labeled>
          </Grid>
        </Grid>
      </div>

      {/* Notes */}
      <div className="w-full border p-6 rounded-xl shadow-xl bg-[#fff3e0] mt-6">
        <h1 className="text-xl mb-4 font-bold flex items-center">
          <EventNoteIcon className="mr-2" /> Notes
        </h1>
        <Divider className="my-4" />
        <Labeled label="Invoice Notes">
          <TextField source="notes" />
        </Labeled>
      </div>
    </SimpleShowLayout>
  </Show>
  );
};



export const AdminInvoiceCreate = (props) => {
   const { permissions } = usePermissions();
   const canEdit = permissions === "admin" || permissions === "operater";
  return (
   !canEdit?undefined: <Create {...props}>
      <SimpleForm>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Create Invoice
            </Typography>
          </Grid>

          {/* Show Seller and Buyer */}
          <NegotiationDetails />
          {/* Negotiation Selection */}
          <Grid item xs={12} md={6}>
            <ReferenceInput source="negotiationId" reference="negotiations" validate={[required()]}>
              <SelectInput optionText="id" label="Negotiation ID" />
            </ReferenceInput>
          </Grid>

          <Grid item xs={12} md={6}>
            <ReferenceInput source="unitId" reference="unit" validate={[required()]}>
              <SelectInput optionText="name" label="Unit" />
            </ReferenceInput>
          </Grid>


          {/* Invoice Fields */}
          <Grid item xs={12} md={6}>
            <NumberInput source="quantity" label="Quantity" validate={[required()]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInput source="amount" label="Amount" validate={[required()]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateInput source="dueDate" label="Due Date" validate={[required()]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="notes" label="Notes" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="deliveryPlace" label="Delivery Place" />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="deliveryResponsibility"
              label="Delivery Responsibility"
              choices={[
                { id: "seller", name: "Seller" },
                { id: "buyer", name: "Buyer" },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput source="paymentTerms" label="Payment Terms" />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="status"
              label="Invoice Status"
              choices={[
                { id: "pending", name: "Pending" },
                { id: "accepted", name: "Accepted" },
                { id: "rejected", name: "Rejected" },
              ]}
              validate={[required()]}
            />
          </Grid>
        </Grid>
      </SimpleForm>
    </Create>
  );
};

// Component to fetch and display seller & buyer details
const NegotiationDetails = () => {
  const negotiationId = useWatch({ name: "negotiation.id" });
  const { data: negotiation } = useGetOne("negotiations", { id: negotiationId }, { enabled: !!negotiationId });

  return negotiation ? (
    <>
      <Grid className="p-4" md={12}>
        {/* Compact Row Layout for Details */}
        <div className="w-full border p-6 rounded-xl shadow-xl bg-white mb-4">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="body1">
                <strong>Seller:</strong> {negotiation.seller?.name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body1">
                <strong>Buyer:</strong> {negotiation.buyer?.name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body1">
                <strong>Type:</strong> {negotiation.type || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body1">
                <strong>Buyer:</strong> {negotiation.tradeType || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body1" className="font-bold">

                <strong>Status:</strong> {negotiation.status || "N/A"}
              </Typography>

            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body1" className="font-bold">
                <strong> Created At:</strong>

              </Typography>


            </Grid>
          </Grid>
        </div>
      </Grid>
    </>
  ) : null;
};

const StatusField = ({ source }) => {
  const record = useRecordContext();
  return (
    <Chip
      label={record[source]}
      color={statusColors[record[source]] || "default"}
      sx={{ fontWeight: "bold" }}
    />
  );
};


export const InvoiceEdit = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const isManager = useIsManager();

  const onSuccess = () => {
    notify('Invoice updated successfully');
    // redirect('show', 'invoices', props.id);
  };

  // Managers have read-only access to invoices
  if (isManager) return null;

  return (
    <Edit {...props}>
      <SimpleForm>
        {/* Invoice Details */}
        <div className="w-full border p-6 rounded-xl shadow-xl bg-[#f0f8ff]">
          <h1 className="text-xl mb-4 font-bold flex items-center">
            <ReceiptIcon className="mr-2" /> Invoice Details
          </h1>
          <Divider className="my-4" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              
                <TextInput source="id" disabled fullWidth />
              
            </Grid>
            <Grid item xs={12} sm={6}>
              
                <SelectInput
                  source="status"
                  choices={[
                    { id: 'pending', name: 'Pending' },
                    { id: 'accepted', name: 'Accepted' },
                    { id: 'rejected', name: 'Rejected' },
                  ]}
                  fullWidth
                />
              
            </Grid>
            <Grid item xs={12} sm={6}>
             
                <DateInput source="created_at" disabled fullWidth />
              
            </Grid>
            <Grid item xs={12} sm={6}>
              
                <DateInput source="dueDate" fullWidth />
              
            </Grid>
            <Grid item xs={12} sm={6}>
              
                <NumberInput source="quantity" fullWidth />
             
            </Grid>
            <Grid item xs={12} sm={6}>
              
                <NumberInput 
                  source="amount" 
                  options={{ style: 'currency', currency: 'USD' }} 
                  fullWidth
                />
             
            </Grid>
          </Grid>
        </div>

        {/* Negotiation Details (Read-only) */}
        <div className="w-full border p-6 rounded-xl shadow-xl bg-[#e8f5e9] mt-6">
          <h1 className="text-xl mb-4 font-bold flex items-center">
            <ShoppingCartIcon className="mr-2" /> Associated Negotiation
          </h1>
          <Divider className="my-4" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              
                <TextInput source="negotiation.id" disabled fullWidth />
              
            </Grid>
            <Grid item xs={12} sm={6}>
              
                <TextInput source="negotiation.tradeType" disabled fullWidth />
              
            </Grid>
          </Grid>
        </div>

        {/* Notes & Rejection Reason */}
        <div className="w-full border p-6 rounded-xl shadow-xl bg-[#fff3e0] mt-6">
          <h1 className="text-xl mb-4 font-bold flex items-center">
            <EventNoteIcon className="mr-2" /> Notes & Additional Info
          </h1>
          <Divider className="my-4" />
          
            <TextInput source="notes" multiline fullWidth />
         
          
            <TextInput 
              source="rejectionReason" 
              multiline 
              fullWidth
              disabled={props.record?.status !== 'rejected'}
            />
          
        </div>
      </SimpleForm>
    </Edit>
  );
};
